import * as React from 'react';
import type { ProgrammaticFilters, ProgrammaticGeoLevel, ProgrammaticMapMode } from '../../types/programmatic';

interface ProgrammaticFilterPanelProps {
  filters: ProgrammaticFilters;
  onFilterChange: (filters: Partial<ProgrammaticFilters>) => void;
  states: string[];
  isLoading: boolean;
}

const GEO_LEVELS: { value: ProgrammaticGeoLevel; label: string }[] = [
  { value: 'states', label: 'States' },
  { value: 'cities', label: 'Cities' },
  { value: 'zipcodes', label: 'Zip Codes' },
];

const MAP_MODES: { value: ProgrammaticMapMode; label: string; description: string }[] = [
  { value: 'native_strength', label: 'Native Strength', description: 'Green = high organic supply' },
  { value: 'paid_reliance', label: 'Paid Reliance', description: 'Red = high paid dependency' },
  { value: 'pressure_score', label: 'Pressure Score', description: 'Red = high demand, low supply' },
];

const ProgrammaticFilterPanel: React.FC<ProgrammaticFilterPanelProps> = ({
  filters,
  onFilterChange,
  states,
  isLoading,
}) => {
  const handleDatePreset = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    onFilterChange({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    });
  };

  return (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-body p-3">
        <div className="row g-3 align-items-end">
          {/* Date Range */}
          <div className="col-lg-2 col-md-3">
            <label className="form-label small fw-semibold mb-1">Start Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.startDate}
              onChange={(e) => onFilterChange({ startDate: e.target.value })}
              disabled={isLoading}
            />
          </div>
          <div className="col-lg-2 col-md-3">
            <label className="form-label small fw-semibold mb-1">End Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.endDate}
              onChange={(e) => onFilterChange({ endDate: e.target.value })}
              disabled={isLoading}
            />
          </div>

          {/* Quick Date Presets */}
          <div className="col-lg-2 col-md-3">
            <label className="form-label small fw-semibold mb-1">Quick Range</label>
            <div className="btn-group btn-group-sm w-100">
              <button className="btn btn-outline-secondary" onClick={() => handleDatePreset(30)} disabled={isLoading}>30d</button>
              <button className="btn btn-outline-secondary" onClick={() => handleDatePreset(90)} disabled={isLoading}>90d</button>
              <button className="btn btn-outline-secondary" onClick={() => handleDatePreset(180)} disabled={isLoading}>6m</button>
            </div>
          </div>

          {/* Geo Level Toggle */}
          <div className="col-lg-2 col-md-3">
            <label className="form-label small fw-semibold mb-1">Geo Level</label>
            <div className="btn-group btn-group-sm w-100">
              {GEO_LEVELS.map((level) => (
                <button
                  key={level.value}
                  className={`btn ${filters.geoLevel === level.value ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => onFilterChange({ geoLevel: level.value, selectedState: null, selectedCity: null })}
                  disabled={isLoading}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>

          {/* State Filter (for city/zip drill-down) */}
          {(filters.geoLevel === 'cities' || filters.geoLevel === 'zipcodes') && (
            <div className="col-lg-2 col-md-3">
              <label className="form-label small fw-semibold mb-1">State Filter</label>
              <select
                className="form-select form-select-sm"
                value={filters.selectedState || ''}
                onChange={(e) => onFilterChange({ selectedState: e.target.value || null, selectedCity: null })}
                disabled={isLoading}
              >
                <option value="">All States</option>
                {states.sort().map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}

          {/* Map Mode */}
          <div className="col-lg-2 col-md-3">
            <label className="form-label small fw-semibold mb-1">Map Color</label>
            <select
              className="form-select form-select-sm"
              value={filters.mapMode}
              onChange={(e) => onFilterChange({ mapMode: e.target.value as ProgrammaticMapMode })}
              disabled={isLoading}
            >
              {MAP_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>{mode.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Map Mode Legend */}
        <div className="mt-2 d-flex align-items-center gap-3">
          <small className="text-muted">
            <i className="fas fa-palette me-1"></i>
            {MAP_MODES.find((m) => m.value === filters.mapMode)?.description}
          </small>
          {filters.selectedState && (
            <button
              className="btn btn-sm btn-outline-secondary py-0"
              onClick={() => onFilterChange({ selectedState: null, selectedCity: null, geoLevel: 'states' })}
            >
              <i className="fas fa-times me-1"></i>Clear state filter
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

ProgrammaticFilterPanel.displayName = 'ProgrammaticFilterPanel';
export default ProgrammaticFilterPanel;
